import { ValidationError } from 'yup';

/**
 * Runs a yup schema and returns errors as a plain { field: message } map.
 *
 * `abortEarly: false` so every problem is reported at once. The default stops
 * at the first failure, which produces the miserable pattern of fixing one
 * field, submitting, and being told about the next.
 */
export async function validateAll(schema, values) {
  try {
    const value = await schema.validate(values, { abortEarly: false, stripUnknown: true });
    return { valid: true, errors: {}, value };
  } catch (error) {
    if (!(error instanceof ValidationError)) throw error;

    const errors = {};
    for (const issue of error.inner.length ? error.inner : [error]) {
      // First message per field wins: later ones are usually consequences of
      // the same mistake, and stacking them under one input is noise.
      if (issue.path && !errors[issue.path]) errors[issue.path] = issue.message;
    }

    return { valid: false, errors, value: undefined };
  }
}

/**
 * Validates a single field, in the context of the whole form.
 *
 * The other values are passed in because a rule can depend on them — a
 * confirmation field, for instance — and validating the field in isolation
 * would make such a rule unable to see what it is comparing against.
 */
export async function validateField(schema, field, values) {
  try {
    await schema.validateAt(field, values);
    return null;
  } catch (error) {
    if (!(error instanceof ValidationError)) throw error;
    return error.message;
  }
}
